import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { goApi, goWebChatApi } from 'api/ApiConfig';
import { v4 as uuidv4 } from 'uuid';

const teamSvcBaseApi = 'api/v1/svc/team/';
const webchatSvcBaseApi = 'api/v1/svc/webchat/';
// fetch user teams
const fetchUserTeams = createAsyncThunk(
  'inbox/web_chat/fetchUserTeams',
  async ({ brand_id }, { rejectWithValue }) => {
    try {
      const res = await goApi.get(
        `/${teamSvcBaseApi}team_member/teams?brand_id=${brand_id}`,
      );

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);
// fetch All Team conversations
const fetchTeamConversations = createAsyncThunk(
  'inbox/web_chat/fetchTeamConversations',
  async (
    {
      brand_id,
      team_id,
      team_member_id,
      excludeListString = '',
      clearExcludeList,
      filter,
      user_id,
      page = 1,
      limit = 10,
    },
    { rejectWithValue, dispatch },
  ) => {
    dispatch(updateSelectedWebChatTeam({ team_id, user_id }));
    // dispatch(resetSelectedWebChatConversation());
    if (clearExcludeList) dispatch(clearConversationExcludeList());
    try {
      let url = `/${webchatSvcBaseApi}web_conversations?brand_id=${brand_id}&team_id=${team_id}&team_member_id=${team_member_id}&page=${page}&limit=${limit}`;
      if (excludeListString) {
        url += `&exclude_ids=${excludeListString}`;
      }
      if (filter) {
        url += `&filter=${filter}`;
      }
      const res = await goWebChatApi.get(url);
      // dispatch(clearConversationExcludeList());

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// fetch single conversation
const fetchTeamConversation = createAsyncThunk(
  'inbox/web_chat/fetchTeamConversation',
  async (
    { brand_id, team_id, conversation_id, team_member_id },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await goWebChatApi.get(
        `/${webchatSvcBaseApi}web_conversation/${conversation_id}?brand_id=${brand_id}&conversation_id=${conversation_id}&team_member_id=${team_member_id}`,
      );
      dispatch(
        updateTeamConversations({ conversation: res?.data?.data, team_id }),
      );
      dispatch(updateConversationExcludeList(conversation_id));
      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// fetch conversations
const fetchWebChatConversationMessages = createAsyncThunk(
  'inbox/web_chat/fetchWebChatConversationMessages',
  async (
    { brand_id, team_id, conversation_id, page = 1, limit },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await goWebChatApi.get(
        `/${webchatSvcBaseApi}web_conversation/messages?brand_id=${brand_id}&team_id=${team_id}&conversation_id=${conversation_id}&page=${page}&limit=${limit}`,
      );
      dispatch(setSelectedWebChatConversation(conversation_id));

      return res?.data?.data || [];
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// assign conversation to team member
const assignConversationToTeamMember = createAsyncThunk(
  'inbox/web_chat/assignConversationToTeamMember',
  async (
    { brand_id, team_id, conversation_id, member_id },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await goWebChatApi.put(
        `/${webchatSvcBaseApi}web_conversation/assignment?brand_id=${brand_id}&team_id=${team_id}&conversation_id=${conversation_id}&member_id=${member_id}`,
      );
      // dispatch(updateSelectedWebChatConversation(res?.data?.data));
      dispatch(
        updateTeamMemberAssignment({
          response: res?.data?.data,
          conversation_id: conversation_id,
          member_id: member_id,
        }),
      );

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// fetch team members
const fetchTeamMembers = createAsyncThunk(
  'inbox/web_chat/fetchTeamMembers',
  async ({ brand_id, team_id }, { rejectWithValue }) => {
    try {
      const res = await goApi.get(
        `/${teamSvcBaseApi}team-members?brand_id=${brand_id}&team_id=${team_id}`,
      );

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// Agent Engagement
const engageAgent = createAsyncThunk(
  'inbox/web_chat/engageAgent',
  async (
    {
      brand_id,
      team_id,
      conversation_id,
      agent_engagement_status,
      team_member_id,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      let query = `brand_id=${brand_id}&team_id=${team_id}&conversation_id=${conversation_id}&agent_engagement_status=${agent_engagement_status}`;
      if (team_member_id) query += `&team_member_id=${team_member_id}`;
      const res = await goWebChatApi.put(
        `/${webchatSvcBaseApi}web_conversation/engage_agent?${query}`,
      );
      const resp = res?.data?.data;
      // dispatch(
      //   updateAgentEngagement({
      //     agent_engagement_status: resp.agent_engagement_status,
      //     conversation_id: resp.conversation_id,
      //   }),
      // );
      // dispatch(updateSelectedWebChatConversation(res?.data?.data));
      return resp;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// Favorite

const favoriteConversation = createAsyncThunk(
  'inbox/web_chat/favoriteConversation',
  async (
    { brand_id, team_id, conversation_id, team_member_id, method = 'put' },
    { rejectWithValue },
  ) => {
    try {
      const res = await goWebChatApi[method](
        `/${webchatSvcBaseApi}web_conversation/favorite?brand_id=${brand_id}&team_id=${team_id}&conversation_id=${conversation_id}&team_member_id=${team_member_id}`,
      );
      return { res: res?.data?.data, conversation_id, team_id, method };
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const initialState = {
  brandsList: [],
  currentBrand: null,
  conversationList: [],
  currentConversation: null,
  openTemplateList: false,

  //

  selectedWebChatTeam: {},
  selectedWebChatConversation: {},

  // ActiveUsers
  activeUsers: [],
  activeEndUsers: [],

  // webChat socket message Alerting
  // webChat_EventStore
  wc_es: {},
  // stack for new conversation requests
  makeNewConversationRequest: [],
  conversationExcludeList: [],
  lastSocketConversationMessage: {},

  // fetch user Teams
  userTeams: [],
  userTeamRole: {},
  userTeamsLoading: false,
  userTeamsError: null,

  // fetch team conversations
  teamConversations: [],
  teamConversationsCount: 0,
  teamConversationsLoading: false,
  teamConversationsError: null,

  // single conversation
  singleConversationLoading: false,
  singleConversationError: null,

  // fetch conversations
  lastMessageId: null,
  webChatMessagesCount: 0,
  webChatConversationMessages: [],
  webChatConversationMessagesLoading: false,
  webChatConversationMessagesError: null,

  // fetch team members
  selectedConversationTeam: null,
  teamMembers: [],
  teamMembersLoading: false,
  teamMembersError: null,

  // Agent Engagement
  agentEngagementLoading: false,
  agentEngagementError: null,

  // Favorite
  favoriteConversationLoading: false,
  favoriteConversationError: null,

  // conversation Filter
  selectedConversationFilter: 'ALCONV',
};
export const authSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    updateBrandsList: (state, action) => {
      state.brandsList = action.payload;
    },
    updateCurrentBrand: (state, action) => {
      state.currentBrand = action.payload;
    },
    updateConversationList: (state, action) => {
      state.conversationList = action.payload;
    },
    updateCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    setOpenTemplateList: (state, action) => {
      state.openTemplateList = action.payload;
    },

    flushWebChat: (state) => {
      state = { ...initialState };
    },

    clearTeamConversations: (state) => {
      state.teamConversations = [];
      state.teamConversationsCount = 0;
    },

    updateSelectedWebChatTeam: (state, { payload }) => {
      const { user_id, team_id } = payload;
      state.selectedWebChatTeam =
        state.userTeams?.find((team) => team.team_id == team_id) || {};
      const index = state.selectedWebChatTeam.team_members.findIndex(
        (member) => member.user_id == user_id,
      );

      state.userTeamRole = state.selectedWebChatTeam.team_members[index];
    },
    resetSelectedWebChatTeam: (state) => {
      state.selectedWebChatTeam = {};
    },
    setSelectedWebChatConversation: (state, action) => {
      state.selectedWebChatConversation =
        state.teamConversations?.find(
          (conversation) => conversation.id === action.payload,
        ) || {};
    },
    updateSelectedWebChatConversation: (state, action) => {
      if (action.payload?.length == 1) {
        state.selectedWebChatConversation = action.payload?.[0];
      }
    },
    resetSelectedWebChatConversation: (state) => {
      state.selectedWebChatConversation = {};
      state.webChatConversationMessages = [];
      state.lastSocketConversationMessage = {};
    },

    // Team Member Assignment updates
    updateTeamMemberAssignment: (state, { payload }) => {
      const { conversation_id, response, member_id } = payload;
      if (state.selectedConversationFilter == 'UASS') {
        state.teamConversations = state.teamConversations.filter(
          (conversation) => conversation.id !== conversation_id,
        );
        state.teamConversationsCount -= 1;
      } else {
        const index = state.teamConversations.findIndex(
          (conversation) => conversation.id === conversation_id,
        );

        state.teamConversations[index].details.assigned_team_member.id =
          member_id;
        if (state.selectedWebChatConversation.id === conversation_id) {
          state.selectedWebChatConversation.details.assigned_team_member.id =
            member_id;
        }
      }
    },

    // update the conversation selection sidebar with a new conversation
    updateTeamConversations: (state, action) => {
      const { conversation, team_id } = action.payload;
      if (team_id == state.selectedWebChatTeam.team_id) {
        state.teamConversations.unshift(conversation);
        state.teamConversationsCount += 1;
      }
    },

    // clear conversation exclude list when user switch to another team
    clearConversationExcludeList: (state) => {
      state.conversationExcludeList = [];
    },

    // update conversation exclude list
    updateConversationExcludeList: (state, action) => {
      state.conversationExcludeList.push(action.payload);
    },

    // update agent engage status for a conversation
    // updateAgentEngagement: (state, { payload }) => {
    // const { agent_engagement_status, conversation_id, team_member_id } =
    //   payload;
    // // Toggling agent engagement status
    // if (
    //   state?.selectedWebChatConversation?.agent_engagement_status != undefined
    // ) {
    //   state.selectedWebChatConversation.agent_engagement_status =
    //     agent_engagement_status;
    // }
    // clearing assigned user for a conversation
    // if (
    //   state?.selectedWebChatConversation?.details?.assigned_team_member?.id
    // ) {
    //   state.selectedWebChatConversation.details.assigned_team_member.id = 0;
    // }
    // if (!state?.teamConversations?.length) return;
    // state.teamConversations = state.teamConversations.map((conversation) => {
    //   if (conversation.id == conversation_id) {
    //     conversation.agent_engagement_status = agent_engagement_status;
    //     conversation.details.assigned_team_member.id = 0;
    //     return conversation;
    //   }
    //   return conversation;
    // });
    // },

    // socket event handlers
    updateActiveUsers: (state, { payload }) => {
      const {
        connection_status,
        sender_id: user_id,
        sender_type: user_type,
      } = payload;
      console.log({ payload });
      const newUser = {
        id: uuidv4(),
        user_id,
        user_type,
        status: connection_status,
      };
      switch (connection_status.toLowerCase()) {
        case 'connected':
          if (user_type.toLowerCase() === 'system_user') {
            const user = state.activeUsers.find(
              (user) => user.user_id == user_id,
            );
            if (!user) state.activeUsers.push(newUser);
          } else if (user_type.toLowerCase() === 'end_user') {
            if (
              state.selectedWebChatConversation.id == payload.conversation_id
            ) {
              // selected conversation
              state.selectedWebChatConversation.connection_details[
                'is_online'
              ] = true;
            }

            if (
              payload.conversation_id &&
              !state.activeEndUsers.includes(payload.conversation_id)
            ) {
              state.activeEndUsers.push(payload.conversation_id);
            }
          }
          break;
        case 'disconnected':
          if (user_type.toLowerCase() === 'system_user') {
            state.activeUsers = state.activeUsers.filter(
              (user) => user.user_id != user_id,
            );
          } else if (user_type.toLowerCase() === 'end_user') {
            if (
              state.selectedWebChatConversation.id == payload.conversation_id
            ) {
              // selected conversation
              state.selectedWebChatConversation.connection_details[
                'is_online'
              ] = false;
            }
            if (
              payload.conversation_id &&
              state.activeEndUsers.includes(payload.conversation_id)
            ) {
              state.activeEndUsers = state.activeEndUsers.filter(
                (user) => user != payload.conversation_id,
              );
            }
          }
          break;
        default:
          console.warn('Invalid type for updateActiveUsers', payload);
          break;
      }
    },

    setConnectedUsersList: (state, { payload }) => {
      const { connected_users } = payload;

      const systemUsers = connected_users
        .filter((user) => user.user_type.toLowerCase() === 'system_user')
        .map((user) => ({
          id: uuidv4(),
          user_id: user.user_id,
          user_type: user.user_type,
          status: user.status,
        }));
      const endUsers = connected_users
        .filter((user) => user.user_type.toLowerCase() === 'end_user')
        .map((user) => ({
          id: uuidv4(),
          user_id: user.user_id,
          user_type: user.user_type,
          status: user.status,
        }));
      state.activeUsers = systemUsers;
      state.activeEndUsers = endUsers;
    },

    // This Handles the chat messages received from the socket
    updateChat: (state, { payload }) => {
      // console.log('payload', payload);
      const team = `team_${payload.team_id}`;
      const conversation = `conversation_${payload.conversation_id}`;
      const chat = payload?.current_message;
      // const hasConversationsList = Array.isArray(
      //   state.wc_es[team]?.['conversations'],
      // );
      const hasTeam = state.wc_es[team];

      const userFocusedTeamID = state.selectedWebChatTeam?.team_id;
      const userFocusedConversationID = state.selectedWebChatConversation?.id;

      const isViewingCurrentTeam =
        userFocusedTeamID === Number(payload.team_id);
      const isViewingCurrentConversation =
        userFocusedConversationID === Number(payload.conversation_id);

      // console.log({
      //   team,
      //   conversation,
      //   chat,
      //   hasTeam,
      //   userFocusedTeamID,
      //   userFocusedConversationID,
      //   isViewingCurrentTeam,
      //   isViewingCurrentConversation,
      // });

      if (isViewingCurrentTeam) {
        if (isViewingCurrentConversation) {
          // console.log('isViewingCurrentConversation');
          state.lastSocketConversationMessage = chat || {};
          state.webChatConversationMessages.unshift(chat);
          state.lastMessageId = chat.id;
          state.selectedWebChatConversation.details.last_message_id = chat.id;
          const convIndex = state.teamConversations.findIndex(
            (item) => item.id == payload.conversation_id,
          );
          if (convIndex !== -1) {
            // updating the sideBar data
            if (!state.teamConversations[convIndex].last_message) {
              state.teamConversations[convIndex].last_message = {};
            }
            state.teamConversations[convIndex].last_message['contact_id'] =
              chat.contact_id;
            state.teamConversations[convIndex].last_message['message'] =
              chat.message;
            state.teamConversations[convIndex].last_message[
              'message_created_at'
            ] = chat.created_at;
            state.teamConversations[convIndex].last_message[
              'participant_type'
            ] = chat.participant_type;
            state.teamConversations[convIndex].last_message['status'] =
              chat.status;
            state.teamConversations[convIndex].last_message['team_member_id'] =
              chat.team_member_id;

            state.teamConversations[convIndex].details.last_message_id =
              chat.id;
            state.teamConversations[convIndex].last_message_time =
              chat.created_at;
          }
        } else {
          // console.log('isViewingCurrentConversation else');
          const conv = state.wc_es[team].conversations?.[conversation];
          if (conv) {
            // console.log('esle 0 conv if', conv);
            // An unread record for team count is not already there or it is 0.
            if (conv.newMessageNotificationCount === 0) {
              // console.log('esle 0 conv if 1');
              // state.wc_es[team].newMessageNotificationCount += 1;
              // state.wc_es[team].conversations[conversation] = {
              //   newMessageNotificationCount: 1,
              // };

              // Get the index of the conversation using the incoming conversation id
              const convIndex = state.teamConversations.findIndex(
                (item) => item.id == payload.conversation_id,
              ); // check if the conversation is in context
              // console.log('convIndex', convIndex);
              // console.log('payload.conversation_id', payload.conversation_id);
              // console.log('state.teamConversations', state.teamConversations);

              if (convIndex == -1) {
                // Not Found
                state.makeNewConversationRequest.push({
                  team_id: payload.team_id,
                  conversation_id: payload.conversation_id,
                  team_member_id: state.selectedWebChatTeam.team_member_id,
                });
                // state.wc_es[team].newMessageNotificationCount += 1;
                // 1. make the REST call
                // 2. Push the new conversation to the top of the list
              } else {
                // Found
                // Found in conversation but not in team unread count
                const teamConversation = state.teamConversations[convIndex];
                // console.log('teamConversation', teamConversation);
                if (!teamConversation.unread_messages_count)
                  teamConversation.unread_messages_count = 0; // if count is not present, initialize it to 0
                teamConversation.unread_messages_count += 1; // conversation unread count++
                teamConversation.last_message_time = chat.updated_at;
                teamConversation.details.last_message_id = chat.id;
                state.wc_es[team].conversations[conversation] = {
                  newMessageNotificationCount: 1,
                };

                if (!teamConversation.last_message) {
                  teamConversation.last_message = {};
                }
                teamConversation.last_message['contact_id'] = chat.contact_id;
                teamConversation.last_message['message'] = chat.message;
                teamConversation.last_message['message_created_at'] =
                  chat.created_at;
                teamConversation.last_message['participant_type'] =
                  chat.participant_type;
                teamConversation.last_message['status'] = chat.status;
                teamConversation.last_message['team_member_id'] =
                  chat.team_member_id;
                state.wc_es[team].newMessageNotificationCount = 1;
              }
            } else {
              // an unread record for team count is already there or it is not 0.
              // console.log('esle 0 conv if else', conv);
              const convIndex = state.teamConversations.findIndex(
                (item) => item.id == payload.conversation_id,
              );
              // console.log('convIndex', convIndex);
              if (convIndex == -1) {
                // Not Found
                state.makeNewConversationRequest.push({
                  team_id: payload.team_id,
                  conversation_id: payload.conversation_id,
                  team_member_id: state.selectedWebChatTeam.team_member_id,
                });
                // 1. make the REST call
                // 2. Push the new conversation to the top of the list
              } else {
                // Found
                // Found in conversation but not in team unread count
                const teamConversation = state.teamConversations[convIndex];
                // console.log('teamConversation', teamConversation);
                if (!teamConversation.unread_messages_count)
                  teamConversation.unread_messages_count = 0; // if count is not present, initialize it to 0
                teamConversation.unread_messages_count += 1; // conversation unread count++
                teamConversation.last_message_time = chat.created_at;
                teamConversation.details.last_message_id = chat.id;
                state.wc_es[team].conversations[conversation] = {
                  newMessageNotificationCount: 1,
                };
                if (!teamConversation.last_message) {
                  teamConversation.last_message = {};
                }
                teamConversation.last_message['contact_id'] = chat.contact_id;
                teamConversation.last_message['message'] = chat.message;
                teamConversation.last_message['message_created_at'] =
                  chat.created_at;
                teamConversation.last_message['participant_type'] =
                  chat.participant_type;
                teamConversation.last_message['status'] = chat.status;
                teamConversation.last_message['team_member_id'] =
                  chat.team_member_id;
                // state.wc_es[team].newMessageNotificationCount += 1;
              }
            }
          } else {
            // a unread record for team count is not there for a conversation.
            const convIndex = state.teamConversations.findIndex(
              (item) => item.id == payload.conversation_id,
            ); // check if the conversation is in context
            // console.log('convIndex', convIndex);
            // console.log('payload.conversation_id', payload.conversation_id);
            // console.log('state.teamConversations', state.teamConversations);

            if (convIndex == -1) {
              // Not Found
              state.makeNewConversationRequest.push({
                team_id: payload.team_id,
                conversation_id: payload.conversation_id,
                team_member_id: state.selectedWebChatTeam.team_member_id,
              });
              state.wc_es[team].newMessageNotificationCount += 1;
              state.wc_es[team].conversations[conversation] = {
                newMessageNotificationCount: 1,
              };
              // 1. make the REST call
              // 2. Push the new conversation to the top of the list
            } else {
              // Found
              // Found in conversation but not in team unread count
              const teamConversation = state.teamConversations[convIndex];
              // console.log('teamConversation', teamConversation);
              if (!teamConversation.unread_messages_count)
                teamConversation.unread_messages_count = 0; // if count is not present, initialize it to 0
              teamConversation.unread_messages_count += 1; // conversation unread count++
              teamConversation.last_message_time = chat.updated_at;
              teamConversation.last_read_message_id = chat.id;
              if (!teamConversation.last_message) {
                teamConversation.last_message = {};
              }
              teamConversation.last_message['contact_id'] = chat.contact_id;
              teamConversation.last_message['message'] = chat.message;
              teamConversation.last_message['message_created_at'] =
                chat.created_at;
              teamConversation.last_message['participant_type'] =
                chat.participant_type;
              teamConversation.last_message['status'] = chat.status;
              teamConversation.last_message['team_member_id'] =
                chat.team_member_id;
              state.wc_es[team].conversations[conversation] = {
                newMessageNotificationCount: 1,
              };
              state.wc_es[team].newMessageNotificationCount += 1;
            }
          }
        }
      } else {
        const conv = state.wc_es[team]?.conversations?.[conversation];
        if (!conv) {
          state.wc_es[team].conversations[conversation] = {
            newMessageNotificationCount: 1,
          };
          state.wc_es[team].newMessageNotificationCount += 1;
        }
      }
    },

    markTeamAsRead: (state, { payload }) => {
      const { team_id, conversation_id } = payload;
      const team = `team_${team_id}`;
      if (!team || !conversation_id) {
        console.error('Invalid team_id or conversation_id');
        return;
      }
      const conversation = `conversation_${conversation_id}`;
      const selectedTeam = state.wc_es[team];

      if (!selectedTeam) return;

      const selectedConversation = selectedTeam.conversations[conversation];
      if (!selectedConversation) return;
      if (selectedTeam.newMessageNotificationCount > 0)
        selectedTeam.newMessageNotificationCount -= 1;
      selectedTeam.conversations[conversation][
        'newMessageNotificationCount'
      ] = 0;
    },

    markConversationAsRead: (state, { payload }) => {
      // if (state?.selectedWebChatConversation?.unread_messages_count){
      //   state?.selectedWebChatConversation?.unread_messages_count = 0
      // }
      const unread_count =
        state?.selectedWebChatConversation?.unread_messages_count;
      if (unread_count) {
        state.selectedWebChatConversation.unread_messages_count = 0;
      }

      state.teamConversations = state.teamConversations.map((conversation) => {
        if (conversation.id == state?.selectedWebChatConversation?.id) {
          if (conversation?.unread_messages_count) {
            conversation.unread_messages_count = 0;
          }
          return conversation;
        }
        return conversation;
      });
    },

    updatedSelectedConversationLastReadMessageId: (state, { payload }) => {
      const { last_read_message_id } = payload;
      state.selectedWebChatConversation.last_read_message_id =
        last_read_message_id;
      // state.selectedWebChatConversation.last_read_message_id =
      //   last_read_message_id;
      const index = state.teamConversations.findIndex(
        (conversation) =>
          conversation.id == state?.selectedWebChatConversation?.id,
      );
      // console.log({ index });
      if (index !== -1) {
        state.teamConversations[index].last_read_message_id =
          last_read_message_id;
      }
    },

    updateMessageStatus: (state, { payload }) => {
      const { conversation_id, current_message_id, type, team_id } = payload;
      // This will updated the message status in the focused conversation
      if (state.selectedWebChatConversation?.id == conversation_id) {
        const messageIndex = state.webChatConversationMessages.findIndex(
          (message) => message.id == current_message_id,
        );
        switch (type) {
          case 'sent':
            if (messageIndex !== -1) {
              state.webChatConversationMessages[messageIndex].status = 5;
            }
            break;
          case 'delivered':
            if (messageIndex !== -1) {
              state.webChatConversationMessages[messageIndex].status = 8;
            }
            break;
          case 'seen':
            if (messageIndex !== -1) {
              state.webChatConversationMessages[messageIndex].status = 10;
              state.webChatConversationMessages
                .filter((message) => message.status < 10 && message.status >= 1)
                .forEach((mes) => {
                  const messageIndex =
                    state.webChatConversationMessages.findIndex(
                      (message) => message.id == mes.id,
                    );
                  state.webChatConversationMessages[messageIndex].status = 10;
                });
            }
            break;
          default:
            console.warn('Unhandled message type:', type);
            break;
        }
      }
      // This will updated the message status Conversation List
      if (state.selectedWebChatTeam?.team_id == team_id) {
        const conversationIndex = state.teamConversations.findIndex(
          (conversation) => conversation.id == conversation_id,
        );
        if (conversationIndex !== -1) {
          if (!state.teamConversations[conversationIndex].last_message) return;
          switch (type) {
            case 'sent':
              state.teamConversations[
                conversationIndex
              ].last_message.status = 5;
              break;
            case 'delivered':
              state.teamConversations[
                conversationIndex
              ].last_message.status = 8;
              break;
            case 'seen':
              state.teamConversations[
                conversationIndex
              ].last_message.status = 10;
              break;
            default:
              console.warn('Unhandled message type:', type);
              break;
          }
        }
      }
    },

    setTeamMembers: (state) => {
      state.teamMembers = state.selectedWebChatTeam?.team_members || [];
    },

    clearTeamMembers: (state) => {
      state.teamMembers = [];
    },

    setSelectedConversationFilter: (state, action) => {
      state.selectedConversationFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserTeams.pending, (state) => {
        state.userTeamsLoading = true;
        state.userTeamsError = null;
      })
      .addCase(fetchUserTeams.fulfilled, (state, action) => {
        state.userTeamsLoading = false;
        const teams = action.payload || [];
        teams.map((team) => {
          const { team_id, unread_conversations_json } = team;
          const unread_conversations = Object.entries(
            unread_conversations_json || {},
          );
          state.wc_es[`team_${team_id}`] = {
            newMessageNotificationCount: unread_conversations.length,
            conversations: unread_conversations.reduce((acc, [c, v]) => {
              return {
                ...acc,
                [c]: { newMessageNotificationCount: v },
              };
            }, {}),
          };
        });
        state.userTeams = teams;

        // state.wc_es
      })
      .addCase(fetchUserTeams.rejected, (state, action) => {
        state.userTeamsLoading = false;
        state.userTeamsError = action.error;
      })
      .addCase(fetchTeamConversations.pending, (state) => {
        state.teamConversationsLoading = true;
        state.teamConversationsError = null;
      })
      .addCase(fetchTeamConversations.fulfilled, (state, action) => {
        state.teamConversationsLoading = false;
        state.teamConversationsCount = action.payload.count;
        state.teamConversations = [
          ...state.teamConversations,
          ...(action.payload.conversations || []),
        ];
      })
      .addCase(fetchTeamConversations.rejected, (state, { payload }) => {
        state.teamConversationsLoading = false;
        state.teamConversationsCount = 0;
        state.teamConversations = [];
        state.teamConversationsError =
          payload?.response?.data?.error || 'Error fetching team conversations';
      })
      .addCase(fetchWebChatConversationMessages.pending, (state) => {
        state.webChatConversationMessagesLoading = true;
        state.webChatConversationMessagesError = null;
      })
      .addCase(fetchWebChatConversationMessages.fulfilled, (state, action) => {
        state.webChatConversationMessagesLoading = false;
        state.webChatMessagesCount = action.payload?.count;
        if (action.payload?.data?.length > 0) {
          state.webChatConversationMessages = [
            ...state.webChatConversationMessages,
            ...action.payload?.data,
          ];
        }
        state.lastMessageId =
          state?.selectedWebChatConversation?.details?.last_message_id;
        if (action.payload?.contact_connection_details?.last_active_at) {
          state.selectedWebChatConversation.details.connection_details =
            action.payload?.contact_connection_details;
        }
      })
      .addCase(fetchWebChatConversationMessages.rejected, (state, action) => {
        state.webChatConversationMessagesLoading = false;
        state.webChatConversationMessagesError = action.error;
      })
      .addCase(fetchTeamMembers.pending, (state) => {
        state.teamMembersLoading = true;
        state.teamMembersError = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.teamMembersLoading = false;
        // state.selectedTeam = action.payload;
        state.teamMembers = action.payload;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.teamMembersLoading = false;
        state.teamMembersError = action.error;
      })
      // .addCase(assignConversationToTeamMember.pending, (state) => {
      // state.assignConversationToTeamMemberLoading = true;
      // state.assignConversationToTeamMemberError = null;
      // })
      // .addCase(assignConversationToTeamMember.fulfilled, (state, action) => {
      // state.assignConversationToTeamMemberLoading = false;
      // state.assignConversationToTeamMember = action.payload;
      // })
      // .addCase(assignConversationToTeamMember.rejected, (state, action) => {
      // state.assignConversationToTeamMemberLoading = false;
      // state.assignConversationToTeamMemberError = action.error;
      // })
      .addCase(engageAgent.pending, (state) => {
        state.agentEngagementLoading = true;
        state.agentEngagementError = null;
      })
      .addCase(engageAgent.fulfilled, (state, action) => {
        const {
          agent_engagement_status,
          conversation_id,
          assigned_to_id: team_member_id,
        } = action.payload;
        // Toggling agent engagement status
        state.agentEngagementLoading = false;
        state.selectedWebChatConversation.agent_engagement_status =
          agent_engagement_status;

        state.selectedWebChatConversation.details['assigned_team_member'][
          'id'
        ] = team_member_id || 0;
        const index = state.teamConversations.findIndex(
          (item) => item.id === conversation_id,
        );
        if (index !== -1) {
          state.teamConversations[index].details['assigned_team_member']['id'] =
            team_member_id || 0;
        }
      })
      .addCase(engageAgent.rejected, (state, action) => {
        state.agentEngagementLoading = false;
        state.agentEngagementError = action.error;
      })
      .addCase(fetchTeamConversation.pending, (state) => {
        state.singleConversationLoading = true;
        state.singleConversationError = null;
      })
      .addCase(fetchTeamConversation.fulfilled, (state, action) => {
        state.singleConversationLoading = false;
        state.makeNewConversationRequest =
          state.makeNewConversationRequest.filter(
            (item) => item.conversation_id !== action.payload.id,
          );
      })
      .addCase(fetchTeamConversation.rejected, (state, action) => {
        state.singleConversationLoading = false;
        state.singleConversationError = action.error;
      })
      .addCase(favoriteConversation.pending, (state) => {
        state.favoriteConversationLoading = true;
      })
      .addCase(favoriteConversation.fulfilled, (state, action) => {
        const { conversation_id, team_id, method } = action.payload;
        state.favoriteConversationLoading = false;
        if (state.selectedWebChatTeam.team_id == team_id) {
          if (method == 'put') {
            const index = state.teamConversations.findIndex(
              (item) => item.id == conversation_id,
            );
            if (index != -1) {
              state.teamConversations[index].favorite_status = 10;
            } else {
              state.makeNewConversationRequest.push({
                team_id: team_id,
                conversation_id: conversation_id,
                team_member_id: state.selectedWebChatTeam.team_member_id,
              });
            }
            if (state.selectedWebChatConversation.id == conversation_id) {
              state.selectedWebChatConversation.favorite_status = 10;
            }
          } else {
            const index = state.teamConversations.findIndex(
              (item) => item.id == conversation_id,
            );
            if (state.selectedConversationFilter == 'FAV') {
              // state.teamConversationsCount -= 1;
              if (index != -1) {
                state.teamConversationsCount -= 1;
                state.teamConversations = state.teamConversations.filter(
                  (item) => item.id != conversation_id,
                );
                if (state.selectedWebChatConversation.id == conversation_id) {
                  state.selectedWebChatConversation.favorite_status = -1;
                }
                if (state.conversationExcludeList.includes(conversation_id)) {
                  state.conversationExcludeList =
                    state.conversationExcludeList.filter(
                      (item) => item != conversation_id,
                    );
                }
              }
            } else {
              if (index != -1) {
                state.teamConversations[index].favorite_status = -1;
              }
              if (state.selectedWebChatConversation.id == conversation_id) {
                state.selectedWebChatConversation.favorite_status = -1;
              }
            }
          }
        }
      })
      .addCase(favoriteConversation.rejected, (state, action) => {
        state.favoriteConversationLoading = false;
        state.favoriteConversationError = action.error;
      });
  },
});

function handleUnrReads() {}

export const {
  updateBrandsList,
  updateCurrentBrand,
  updateConversationList,
  updateCurrentConversation,
  setOpenTemplateList,
  clearTeamConversations,
  updateSelectedWebChatTeam,
  setSelectedWebChatConversation,
  updateSelectedWebChatConversation,
  resetSelectedWebChatTeam,
  resetSelectedWebChatConversation,
  updateTeamMemberAssignment,
  setTeamMembers,
  clearTeamMembers,
  setSelectedConversationFilter,
  // socket event handlers
  updateMessageStatus,
  updateActiveUsers,
  setConnectedUsersList,
  updateChat,
  markTeamAsRead,
  markConversationAsRead,
  // updateAgentEngagement,
  flushWebChat,
  updateTeamConversations,
  clearConversationExcludeList,
  updateConversationExcludeList,
  updatedSelectedConversationLastReadMessageId,
} = authSlice.actions;

export default authSlice.reducer;

export {
  fetchUserTeams,
  fetchTeamConversations,
  fetchTeamConversation,
  fetchWebChatConversationMessages,
  fetchTeamMembers,
  assignConversationToTeamMember,
  engageAgent,
  favoriteConversation,
};
